import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha,
  Paper,
} from "@mui/material";
import {
  CheckCircle,
  Star,
  BusinessCircle,
  VpnKey,
  SupportAgent,
  Explore,
} from "@mui/icons-material";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import StudentSidebar from "./StudentSdebar";

const Membership = () => {
  const theme = useTheme();
  const [hoveredPlan, setHoveredPlan] = useState(null);
  const [sidebarExpanded, setSidebarExpanded] = useState(true);

  const plans = [
    {
      title: "Basic Plan",
      price: "₹499",
      period: "/year",
      features: [
        "Access to foundational courses",
        "Downloadable learning resources",
        "Community forum access",
        "Email support",
      ],
      featured: false,
    },
    {
      title: "Pro Plan",
      price: "₹1499",
      period: "/year",
      features: [
        "Unlimited access to all courses",
        "Completion certificates",
        "1-on-1 mentor support",
        "Access to exclusive live workshops",
        "Priority support",
      ],
      featured: true,
    },
    {
      title: "Enterprise Plan",
      price: "Custom",
      period: " Pricing",
      features: [
        "All Pro features for your team",
        "Dedicated team dashboards",
        "Custom learning paths",
        "Advanced progress analytics",
      ],
      featured: false,
    },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
        bgcolor: "background.default",
        color: "text.primary",
      }}
    >
      <Header />

      <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden" }}>
        <StudentSidebar onToggle={setSidebarExpanded} />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, md: 4 },
            width: sidebarExpanded ? "calc(100% - 280px)" : "calc(100% - 80px)",
            transition: theme.transitions.create("width", { duration: 300 }),
            height: "calc(100vh - 64px)",
            overflowY: "auto",
          }}
        >
          <Container maxWidth="lg">
            {/* Header Section */}
            <Box sx={{ textAlign: "center", mb: 10 }}>
              <Typography
                variant="h2"
                fontWeight={900}
                gutterBottom
                sx={{
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, #D4145A)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontSize: { xs: "2.5rem", md: "3.5rem" },
                }}
              >
                Choose Your Potential
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ maxWidth: "800px", mx: "auto", lineHeight: 1.6 }}
              >
                Unlock elite learning experiences with our flexible membership
                plans. Get unlimited access to expert-led content and joining
                our vibrant global community.
              </Typography>
            </Box>

            {/* Pricing Grid */}
            <Grid
              container
              spacing={4}
              alignItems="stretch"
              justifyContent="center"
            >
              {plans.map((plan, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card
                    onMouseEnter={() => setHoveredPlan(index)}
                    onMouseLeave={() => setHoveredPlan(null)}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: "30px",
                      border: "1px solid",
                      borderColor: plan.featured ? "primary.main" : "divider",
                      position: "relative",
                      transition:
                        "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                      transform: plan.featured ? "scale(1.05)" : "none",
                      boxShadow: plan.featured
                        ? `0 20px 40px ${alpha(theme.palette.primary.main, 0.15)}`
                        : "0 10px 30px rgba(0,0,0,0.05)",
                      "&:hover": {
                        transform: plan.featured
                          ? "scale(1.08)"
                          : "translateY(-10px)",
                        boxShadow: `0 30px 60px ${alpha(theme.palette.primary.main, 0.2)}`,
                      },
                    }}
                  >
                    {plan.featured && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 20,
                          right: 20,
                          bgcolor: "primary.main",
                          color: "white",
                          px: 2,
                          py: 0.5,
                          borderRadius: "10px",
                          fontSize: "0.75rem",
                          fontWeight: 800,
                          letterSpacing: "1px",
                        }}
                      >
                        MOST POPULAR
                      </Box>
                    )}

                    <CardContent
                      sx={{
                        p: 5,
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Typography
                        variant="h5"
                        fontWeight={800}
                        gutterBottom
                        color={plan.featured ? "primary" : "text.primary"}
                      >
                        {plan.title}
                      </Typography>

                      <Box
                        sx={{ mb: 4, display: "flex", alignItems: "baseline" }}
                      >
                        <Typography variant="h3" fontWeight={900}>
                          {plan.price}
                        </Typography>
                        <Typography
                          variant="subtitle1"
                          color="text.secondary"
                          sx={{ ml: 0.5 }}
                        >
                          {plan.period}
                        </Typography>
                      </Box>

                      <List sx={{ mb: 4, flexGrow: 1 }}>
                        {plan.features.map((feature, idx) => (
                          <ListItem key={idx} disableGutters sx={{ py: 1 }}>
                            <ListItemIcon
                              sx={{ minWidth: 36, color: "primary.main" }}
                            >
                              <CheckCircle fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={feature}
                              primaryTypographyProps={{
                                variant: "body2",
                                fontWeight: 600,
                                color: "text.secondary",
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>

                      <Button
                        fullWidth
                        variant={plan.featured ? "contained" : "outlined"}
                        size="large"
                        sx={{
                          borderRadius: "50px",
                          py: 2,
                          fontWeight: 800,
                          fontSize: "1.1rem",
                          boxShadow: plan.featured
                            ? "0 8px 25px rgba(212, 175, 55, 0.3)"
                            : "none",
                        }}
                      >
                        Join Now
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Contact Section */}
            <Box sx={{ mt: 15, textAlign: "center" }}>
              <Paper
                elevation={0}
                sx={{
                  p: 6,
                  borderRadius: "30px",
                  bgcolor: alpha(theme.palette.primary.main, 0.03),
                  border: "1px solid",
                  borderColor: alpha(theme.palette.primary.main, 0.1),
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={4}
                  alignItems="center"
                  justifyContent="center"
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <SupportAgent color="primary" sx={{ fontSize: "3rem" }} />
                    <Box sx={{ textAlign: "left" }}>
                      <Typography variant="h6" fontWeight={800}>
                        Still Have Questions?
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Our support team is here to help you choose the right
                        path.
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    component="a"
                    href="mailto:support@kaas.com"
                    variant="outlined"
                    size="large"
                    sx={{ borderRadius: "50px", px: 5, fontWeight: 700 }}
                  >
                    Contact Support
                  </Button>
                </Stack>
              </Paper>
            </Box>
          </Container>
          <Footer />
        </Box>
      </Box>
    </Box>
  );
};

export default Membership;
